import { useEffect, useState } from 'react'
import Box from '@mui/joy/Box'
import Stack from '@mui/joy/Stack'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import Chip from '@mui/joy/Chip'
import Button from '@mui/joy/Button'
import Divider from '@mui/joy/Divider'
import Tooltip from '@mui/joy/Tooltip'
import Tabs from '@mui/joy/Tabs'
import TabList from '@mui/joy/TabList'
import Tab from '@mui/joy/Tab'
import TabPanel from '@mui/joy/TabPanel'

import VideocamIcon from '@mui/icons-material/Videocam'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import QueueIcon from '@mui/icons-material/Queue'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import SettingsIcon from '@mui/icons-material/Settings'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import LinkIcon from '@mui/icons-material/Link'

import { useCalendarStatus } from '../hooks/useCalendarStatus'
import { useCurrentUserId } from '../hooks/useCurrentUserId'

import { QueueTab } from './meeting-recorder/QueueTab'
import { UpcomingTab } from './meeting-recorder/UpcomingTab'
import { CalendarsTab } from './meeting-recorder/CalendarsTab'
import { CostTab } from './meeting-recorder/CostTab'
import { SettingsTab } from './meeting-recorder/SettingsTab'
import { RecordCallModal } from './meeting-recorder/RecordCallModal'
import { ConnectCalendarModal } from './meeting-recorder/ConnectCalendarModal'

/**
 * Meeting Recorder dashboard, rendered inside CRM → Setup beneath DeveloperPanel.
 *
 * Layout philosophy: match the rest of CRM (Joy components, Sheet+Tabs+Table) so
 * the look stays coherent and adapts to light/dark mode via Joy's CssVarsProvider.
 *
 *   ┌─ header ─────────────────────────────────────────────────────────────┐
 *   │  Meeting Recorder  [calendar: connected]      [Record] [Connect cal] │
 *   └──────────────────────────────────────────────────────────────────────┘
 *   ┌─ tabs: Queue • Upcoming • Calendars • Cost • Settings ──────────────┐
 *   │                                                                       │
 *   │  Queue tab: split-pane (table left, CallDetail right when selected)   │
 *   │  Upcoming: list of calendar events with qualifies flag               │
 *   │  Calendars: rep calendar connection health                            │
 *   │  Cost: month-to-date metrics                                          │
 *   │  Settings: workspace config + roster (accordion)                      │
 *   └──────────────────────────────────────────────────────────────────────┘
 *
 * Quick actions live in the header as modals so the daily-ops surfaces
 * (Record this call, Connect calendar) are one click away from any tab.
 */
export default function MeetingRecorderConsole() {
    const userId = useCurrentUserId()
    const { data: cals, refetch: refetchCal } = useCalendarStatus(userId)

    const [tabIndex, setTabIndex] = useState(0)
    const [recordOpen, setRecordOpen] = useState(false)
    const [connectOpen, setConnectOpen] = useState(false)

    // When the Queue tab is active, child controls a selected botId.
    const [selectedBotId, setSelectedBotId] = useState<string | null>(null)
    const [queueRefreshNonce, setQueueRefreshNonce] = useState(0)

    // If a calendar got connected/disconnected via the modal, refresh the chip.
    useEffect(() => {
        if (!connectOpen) {
            void refetchCal()
        }
    }, [connectOpen, refetchCal])

    // Aggregate status across N calendars: worst wins (REVOKED > DISCONNECTED > CONNECTED).
    const statusPriority: Record<string, number> = { REVOKED: 3, DISCONNECTED: 2, CONNECTED: 1 }
    const aggregateStatus: 'CONNECTED' | 'DISCONNECTED' | 'REVOKED' | 'NONE' =
        cals.length === 0
            ? 'NONE'
            : cals.reduce(
                  (worst, c) => ((statusPriority[c.status] ?? 0) > (statusPriority[worst] ?? 0) ? c.status : worst),
                  'CONNECTED' as 'CONNECTED' | 'DISCONNECTED' | 'REVOKED'
              )
    const calChipColor: 'success' | 'danger' | 'neutral' | 'warning' =
        aggregateStatus === 'CONNECTED' ? 'success' : aggregateStatus === 'REVOKED' ? 'danger' : aggregateStatus === 'DISCONNECTED' ? 'warning' : 'neutral'
    const calChipLabel =
        cals.length === 0
            ? 'Calendar: Not connected'
            : cals.length === 1
              ? `Calendar: ${aggregateStatus.toLowerCase()}`
              : `${cals.length} calendars · ${aggregateStatus.toLowerCase()}`
    const calChipTooltip =
        cals.length === 0
            ? 'No calendar connected'
            : cals.length === 1
              ? (cals[0].account_email ?? '')
              : cals.map((c) => `${c.account_email} — ${c.status}`).join('\n')

    return (
        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)' }}>
            <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                {/* Header */}
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    gap={1.5}
                    sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.level1' }}
                >
                    <Stack direction="row" alignItems="center" gap={1.5}>
                        <VideocamIcon color="primary" />
                        <Stack gap={0.25}>
                            <Typography level="title-md">Meeting Recorder</Typography>
                            <Typography level="body-xs" color="neutral">
                                Google Meet / Zoom / Teams · auto-record qualifying calls
                            </Typography>
                        </Stack>
                        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{calChipTooltip}</span>} placement="bottom">
                            <Chip
                                size="sm"
                                color={calChipColor}
                                variant="soft"
                                startDecorator={aggregateStatus === 'CONNECTED' ? <LinkIcon fontSize="small" /> : <LinkOffIcon fontSize="small" />}
                                sx={{ ml: 1 }}
                            >
                                {calChipLabel}
                            </Chip>
                        </Tooltip>
                    </Stack>

                    <Stack direction="row" gap={1}>
                        <Button size="sm" color="danger" variant="solid" startDecorator={<FiberManualRecordIcon />} onClick={() => setRecordOpen(true)}>
                            Record this call
                        </Button>
                        <Button
                            size="sm"
                            color={cals.length > 0 ? 'neutral' : 'primary'}
                            variant={cals.length > 0 ? 'outlined' : 'solid'}
                            startDecorator={<CalendarMonthIcon />}
                            onClick={() => setConnectOpen(true)}
                        >
                            {cals.length > 0 ? 'Manage calendars' : 'Connect calendar'}
                        </Button>
                    </Stack>
                </Stack>

                {/* Tabs */}
                <Tabs
                    value={tabIndex}
                    onChange={(_, v) => {
                        const next = v as number
                        setTabIndex(next)
                        if (next !== 0) setSelectedBotId(null)
                    }}
                    sx={{ bgcolor: 'transparent', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                >
                    <TabList sx={{ px: 1.5, pt: 1, flexShrink: 0 }}>
                        <Tab value={0}>
                            <Stack direction="row" alignItems="center" gap={0.75}>
                                <QueueIcon fontSize="small" /> Queue
                            </Stack>
                        </Tab>
                        <Tab value={1}>
                            <Stack direction="row" alignItems="center" gap={0.75}>
                                <EventAvailableIcon fontSize="small" /> Upcoming
                            </Stack>
                        </Tab>
                        <Tab value={2}>
                            <Stack direction="row" alignItems="center" gap={0.75}>
                                <AttachMoneyIcon fontSize="small" /> Cost
                            </Stack>
                        </Tab>
                        <Tab value={3}>
                            <Stack direction="row" alignItems="center" gap={0.75}>
                                <SettingsIcon fontSize="small" /> Settings
                            </Stack>
                        </Tab>
                    </TabList>

                    <TabPanel value={0} sx={{ p: 2 }}>
                        <QueueTab
                            selectedBotId={selectedBotId}
                            onSelect={(id) => setSelectedBotId(id === selectedBotId ? null : id)}
                            refreshNonce={queueRefreshNonce}
                        />
                    </TabPanel>
                    <TabPanel value={1} sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                        <Stack gap={2} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ flexShrink: 0 }}>
                                <CalendarsTab />
                            </Box>
                            <Divider sx={{ flexShrink: 0 }} />
                            <UpcomingTab />
                        </Stack>
                    </TabPanel>
                    <TabPanel value={2} sx={{ p: 2 }}>
                        <CostTab />
                    </TabPanel>
                    <TabPanel value={3} sx={{ p: 2 }}>
                        <SettingsTab />
                    </TabPanel>
                </Tabs>
            </Sheet>

            <RecordCallModal
                open={recordOpen}
                onClose={() => setRecordOpen(false)}
                onCreated={(botId) => {
                    setRecordOpen(false)
                    setTabIndex(0)
                    setSelectedBotId(botId)
                    setQueueRefreshNonce((n) => n + 1)
                }}
            />
            <ConnectCalendarModal open={connectOpen} onClose={() => setConnectOpen(false)} />
        </Box>
    )
}
